(function () {
  const SELECTORS = {
    wrapper: '[data-cart-upsell]',
    heading: '[data-cart-upsell-heading]',
    results: '[data-cart-upsell-results]',
    template: '[data-cart-upsell-card-template]',
    optionTemplate: '[data-cart-upsell-option-template]',
  };

  const INITIALIZED_ATTRIBUTE = 'data-cart-upsell-initialized';

  function getRoutesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function parseBoolean(value) {
    return value === true || value === 'true';
  }

  function parseProductIds(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed.map(Number).filter(Boolean) : [];
    } catch (error) {
      return [];
    }
  }

  function uniqueById(products) {
    const seen = new Set();
    return products.filter((product) => {
      if (!product || !product.id || seen.has(product.id)) return false;
      seen.add(product.id);
      return true;
    });
  }

  function stripHtml(value) {
    const element = document.createElement('div');
    element.innerHTML = value || '';
    return element.textContent.trim();
  }

  function truncate(value, limit) {
    if (!value || value.length <= limit) return value;
    return `${value.slice(0, limit).trim()}...`;
  }

  function getAvailableVariants(product) {
    return Array.isArray(product.variants) ? product.variants.filter((variant) => variant && variant.available) : [];
  }

  function getVariantPrice(variant) {
    return Number(variant && variant.price) || 0;
  }

  function isGiftCard(product) {
    if (product.gift_card) return true;
    const handle = String(product.handle || '').toLowerCase();
    const type = String(product.type || product.product_type || '').toLowerCase();
    const tags = Array.isArray(product.tags) ? product.tags.join(' ').toLowerCase() : '';
    return handle.includes('gift') || type.includes('gift') || tags.includes('gift');
  }

  function isEligibleProduct(product, cartProductIds) {
    if (!product || cartProductIds.has(Number(product.id))) return false;
    if (isGiftCard(product)) return false;

    const availableVariants = getAvailableVariants(product);
    if (!availableVariants.length) return false;

    return availableVariants.some((variant) => getVariantPrice(variant) > 0);
  }

  function formatMoney(cents, currency) {
    const amount = (Number(cents) || 0) / 100;

    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || 'CZK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (error) {
      return `${amount.toFixed(0)} ${currency || ''}`.trim();
    }
  }

  function getImageUrl(product) {
    if (product.featured_image) {
      if (typeof product.featured_image === 'string') return product.featured_image;
      if (product.featured_image.url) return product.featured_image.url;
      if (product.featured_image.src) return product.featured_image.src;
    }

    if (Array.isArray(product.images) && product.images.length) {
      return typeof product.images[0] === 'string' ? product.images[0] : product.images[0].src || product.images[0].url || '';
    }

    return '';
  }

  function getProductUrl(product) {
    if (product.url) return product.url;
    if (product.handle) return `${getRoutesRoot()}products/${product.handle}`;
    return '#';
  }

  function getProductMeta(product, settings) {
    if (settings.showVendor && product.vendor) return product.vendor;
    if (settings.showDescription) {
      const description = stripHtml(product.body_html || product.description || '');
      if (description) return truncate(description, 90);
    }
    return product.type || product.product_type || '';
  }

  function createCheckbox(id, label, price, settings, template) {
    const fragment = template.content.cloneNode(true);
    const option = fragment.querySelector('.cart-upsell-card__option');
    const checkbox = fragment.querySelector('.cart-upsell-card__checkbox');
    const labelElement = fragment.querySelector('.cart-upsell-card__option-label');
    const priceElement = fragment.querySelector('.cart-upsell-card__price');

    checkbox.value = id;
    labelElement.textContent = label || settings.buttonLabel;
    priceElement.textContent = formatMoney(price, settings.currency);

    return option;
  }

  function renderProduct(product, wrapper, settings) {
    const template = wrapper.querySelector(SELECTORS.template);
    const optionTemplate = wrapper.querySelector(SELECTORS.optionTemplate);
    if (!template || !optionTemplate) return null;

    const availableVariants = getAvailableVariants(product).filter((variant) => getVariantPrice(variant) > 0);
    if (!availableVariants.length) return null;

    const fragment = template.content.cloneNode(true);
    const card = fragment.querySelector('[data-cart-upsell-card]');
    const title = fragment.querySelector('[data-cart-upsell-title]');
    const image = fragment.querySelector('[data-cart-upsell-image]');
    const imageLink = fragment.querySelector('[data-cart-upsell-image-link]');
    const meta = fragment.querySelector('[data-cart-upsell-meta]');
    const options = fragment.querySelector('[data-cart-upsell-options]');
    const productUrl = getProductUrl(product);
    const imageUrl = getImageUrl(product);

    title.textContent = product.title || '';
    title.href = productUrl;
    imageLink.href = productUrl;

    if (imageUrl) {
      image.src = imageUrl;
      image.alt = product.title || '';
    } else {
      image.hidden = true;
    }

    const metaText = getProductMeta(product, settings);
    if (metaText) {
      meta.textContent = metaText;
      meta.hidden = false;
    }

    const variantsToRender = availableVariants.length > 1 ? availableVariants.slice(0, 2) : availableVariants.slice(0, 1);

    variantsToRender.forEach((variant) => {
      const label = availableVariants.length > 1 ? variant.title : settings.buttonLabel;
      options.append(createCheckbox(variant.id, label, getVariantPrice(variant), settings, optionTemplate));
    });

    return card;
  }

  async function fetchRecommendations(productId, intent) {
    const url = `${getRoutesRoot()}recommendations/products.json?product_id=${productId}&limit=10&intent=${intent}`;

    try {
      const response = await fetch(url);
      if (!response.ok) return [];
      const data = await response.json();
      return Array.isArray(data.products) ? data.products : [];
    } catch (error) {
      return [];
    }
  }

  async function getRecommendationsForProduct(productId, enableRelatedFallback) {
    const complementary = await fetchRecommendations(productId, 'complementary');
    if (complementary.length || !enableRelatedFallback) return complementary;
    return fetchRecommendations(productId, 'related');
  }

  async function addVariantToCart(variantId) {
    const response = await fetch(`${getRoutesRoot()}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: [
          {
            id: Number(variantId),
            quantity: 1,
          },
        ],
      }),
    });

    if (!response.ok) throw new Error('Unable to add upsell product to cart.');
    return response.json();
  }

  function bindAddToCart(wrapper) {
    wrapper.addEventListener('change', async (event) => {
      const checkbox = event.target.closest('.cart-upsell-card__checkbox');
      if (!checkbox || !checkbox.checked || checkbox.disabled) return;

      checkbox.disabled = true;

      try {
        await addVariantToCart(checkbox.value);
        window.location.reload();
      } catch (error) {
        checkbox.checked = false;
        checkbox.disabled = false;
        console.error(error);
      }
    });
  }

  async function initWrapper(wrapper) {
    if (!wrapper || wrapper.hasAttribute(INITIALIZED_ATTRIBUTE)) return;
    wrapper.setAttribute(INITIALIZED_ATTRIBUTE, 'true');

    const results = wrapper.querySelector(SELECTORS.results);
    if (!results) return;

    const cartProductIds = new Set(parseProductIds(wrapper.dataset.productIds));
    if (!cartProductIds.size) return;

    const settings = {
      limit: Math.max(1, Number(wrapper.dataset.limit) || 4),
      heading: wrapper.dataset.heading || '',
      enableRelatedFallback: parseBoolean(wrapper.dataset.enableRelatedFallback),
      buttonLabel: wrapper.dataset.buttonLabel || '',
      showVendor: parseBoolean(wrapper.dataset.showVendor),
      showDescription: parseBoolean(wrapper.dataset.showDescription),
      currency: wrapper.dataset.currency || 'CZK',
    };

    const heading = wrapper.querySelector(SELECTORS.heading);
    if (heading && settings.heading) heading.textContent = settings.heading;

    bindAddToCart(wrapper);

    const productGroups = await Promise.all(
      Array.from(cartProductIds).map((productId) => getRecommendationsForProduct(productId, settings.enableRelatedFallback))
    );
    const recommendedProducts = uniqueById(productGroups.flat())
      .filter((product) => isEligibleProduct(product, cartProductIds))
      .slice(0, settings.limit);

    if (!recommendedProducts.length) {
      wrapper.hidden = true;
      return;
    }

    const renderedCards = recommendedProducts
      .map((product) => renderProduct(product, wrapper, settings))
      .filter(Boolean);

    if (!renderedCards.length) {
      wrapper.hidden = true;
      return;
    }

    results.replaceChildren(...renderedCards);
    wrapper.hidden = false;
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.wrapper).forEach(initWrapper);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  const observer = new MutationObserver(initAll);
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
